import { useAppStore } from '@/stores/app-store';
import { API_BASE } from '@/config';

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

function getHeaders(): Record<string, string> {
  const token = useAppStore.getState().token;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = useAppStore.getState().refreshToken;
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.token) {
      useAppStore.getState().setAuth(data.token, refreshToken);
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
}

async function request<T>(path: string, options?: RequestInit, retry = true): Promise<T> {
  const url = `${API_BASE}${path}`;
  const res = await fetch(url, {
    ...options,
    headers: { ...getHeaders(), ...options?.headers },
  });

  if (res.status === 401 && retry && path !== '/auth/refresh') {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = doRefresh().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      return request<T>(path, options, false);
    }
    // Token rejected and refresh failed — prompt the user to re-authenticate.
    useAppStore.getState().expireSession();
  }

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${res.status}: ${text}`);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'DELETE', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
};

export interface DashboardTemplateItem {
  id: string;
  name: string;
  description: string | null;
  createdAt?: string;
  updatedAt?: string;
  defaultDashboardName: string | null;
  defaultDashboardSortOrder: number | null;
  defaultDashboardDescription: string | null;
  defaultIsFilterOpen: boolean | null;
  displayFilterOpen: boolean | null;
  tenantType: string | null;
  typeOfTenantID: string;
  dashboards: {
    items: Array<{ id: string; name: string; tenantID: string; sortOrder: number | null; description: string | null }>;
    nextToken: string | null;
  };
  fixedFilters: Array<{ key: string; label: string; fixedType: string; cluvioFilterID: string | null }>;
  variableFilters: Array<{ key: string; label: string; fieldType: string; cluvioFilterID: string | null; limitFilterOptions: boolean | null }>;
  cluvioDashboard?: { id: string; type: string; dashboardAttributes: Record<string, unknown> };
  cluvioSharingToken?: { id: string; token: string; dashboard_id: string };
}

export interface TenantItem {
  id: string;
  name: string;
  typeOfTenantID: string;
}

export interface TenantDetail {
  id: string;
  name: string;
  typeOfTenantID: string;
  createdAt: string;
  updatedAt: string;
  dashboards: {
    items: Array<{
      id: string;
      name: string;
      sortOrder: number | null;
      description: string | null;
      url: string;
      tenantID: string;
      typeOfTenantID: string;
      templateID: string;
      fixedParameters: Array<unknown>;
      template: { name: string } | null;
      variableFilterOverrides: Array<unknown>;
      createdAt: string;
      updatedAt: string;
    }>;
    nextToken: string | null;
  };
  members: {
    items: Array<{
      id: string;
      profileID: string;
      tenantRole: string | null;
      profile: { id: string; email: string; firstname: string | null; lastname: string | null };
    }>;
    nextToken: string | null;
  };
  documents: { items: TenantDocument[]; nextToken: string | null };
  analyticsDashboards: { items: Array<{ id: string; shopAnalytics: { id: string; title: string | null } | null }> };
  inventoryDashboards: { items: Array<{ id: string; inventoryGroup: { id: string; title: string | null } | null }> };
}

export interface TenantDocument {
  id: string;
  documentKind: string; // FILE | LINK
  privacyLevel: string; // PUBLIC | PROTECTED
  title: string | null;
  description: string | null;
  sortOrder: number | null;
  createdAt?: string;
  file: { fileName: string | null; fileSizeInBytes: number | null; contentType: string | null } | null;
  link: { url: string | null } | null;
}

export interface ProfileSearchItem {
  id: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
}

export interface MembershipSummary {
  id: string;
  tenantID: string;
  typeOfTenantID: string | null;
  tenantRole: string | null;
}

export interface UserListItem {
  id: string;
  email: string;
  firstname: string | null;
  lastname: string | null;
  roleDescription: string | null;
  joinedAt: string | null;
  lastInvitedAt: string | null;
  lastAuthenticatedAt: string | null;
  cognitoUserStatus: string | null;
  tenants: { items: MembershipSummary[] };
}

export interface UserDetail extends Omit<UserListItem, 'tenants'> {
  phone: string | null;
  lastSelectedTenantId: string | null;
  tenants: {
    items: Array<MembershipSummary & { tenant: { id: string; name: string } | null }>;
  };
}

export interface CluvioDashboardInfo {
  id: string;
  name: string;
  filters: Array<{ id: string; name: string; variable_name: string; controlType?: string }>;
  has_timerange_editor: boolean;
  has_time_aggregation: boolean;
  sharing_token: { id: string; token: string; name: string };
}

export interface FilterOption {
  label: string;
  value: string;
}
